import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getLocalStorage, setLocalStorage } from "../../../helpers/utils";
import {
  getCrrResults,
  getEmployeeDashboardData,
  getMilMasterData,
  getProgressGraphData,
} from "../../../slices/Employee/employeeDashboard.slice";
import { notification } from "antd";
import "./Dashboard.scss";
// Kept: this page still uses antd `notification` (imperative), which needs
// antd's stylesheet. antd.css is bundled globally regardless, but importing it
// here keeps this page self-consistent.
import "antd/dist/antd.css";
import { getSurveyStatus } from "../../../slices/Employee/notification.slice";
import moment from "moment";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ErrorAlert from "../../../components/UI/ErrorAlert/ErrorAlert";
import { Button, Card, Skeleton } from "../../../components/ds";
import ChartSkeleton from "../../../components/OrgAdminGraph/ChartSkeleton";
import DashboardGraph from "../../Admin/DashboardOrgAdmin/DashboardGraph";
import { setSelectedDomain } from "../../../slices/Employee/survey-CRR.slice";
import { setAssessmentComplete } from "../../../slices/Admin/survey-CSF.slice";
import { SURVEYS } from "../../../helpers/constants";
import { setActiveSurvey } from "../../../slices/signIn.slice";

/**
 * Loading skeleton mirroring the employee dashboard's two-column layout
 * (domain list + analytics) so the region does not jump when data arrives.
 */
function DashboardSkeleton() {
  return (
    <div className="employee-dashboard-grid employee-dashboard-enter" aria-hidden="true">
      <div className="employee-dashboard-col employee-dashboard-col--list">
        <Skeleton width={220} height={16} style={{ marginBottom: 12 }} />
        <div className="domain-cards-container">
          {[0, 1, 2, 3, 4].map((i) => (
            <Card key={i} className="dashboard-domains-list" style={{ marginTop: 10 }}>
              <Skeleton width="70%" height={16} />
            </Card>
          ))}
        </div>
      </div>
      <div className="employee-dashboard-col employee-dashboard-col--graph">
        <Card className="dashboard-graph-container">
          <Skeleton width={180} height={18} style={{ marginBottom: 12 }} />
          <ChartSkeleton variant="line" />
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    employeeDashboardData,
    employeeDashboardError,
    employeeDashboardLoading,
    milMasterData,
  } = useSelector((state) => state.employeeDashboard);

  const { profileEditResponse } = useSelector(
    (state) => state.editEmployeeProfile
  );

  const { csf_assessment_id, csf_assessment_status } = useSelector(
    (state) => state.notification.surveyStatus
  );

  const [selectedDomainIndex, setSelectedDomainIndex] = useState("");
  const selectedSurvey = getLocalStorage("SELECTED-SURVEY")?.id || "";
  const userData = getLocalStorage("USER_DETAILS");
  const [render, setRender] = useState(true);

  useEffect(() => {
    setRender((prev) => !prev);
  }, [profileEditResponse]);

  useEffect(() => {
    dispatch(getEmployeeDashboardData(selectedSurvey));
    dispatch(getCrrResults());
    milMasterData.length == 0 && dispatch(getMilMasterData());
    dispatch(getProgressGraphData());
  }, [dispatch]);

  useEffect(() => {
    initNotifications();
  }, []);

  const initNotifications = () => {
    dispatch(getSurveyStatus()).then((data) => {
      const surveyStatuses = data.payload.data;
      if (surveyStatuses) {
        setLocalStorage("SELECTED-SURVEY-STATUS", {
          status: surveyStatuses?.survey_status,
        });
        const rejectionShown = sessionStorage.getItem("REJECTIONS_SHOWN");
        if (
          !rejectionShown && //show only once per session
          surveyStatuses?.survey_rejections?.length > 0 &&
          surveyStatuses?.survey_rejections[0] != null
        ) {
          const notifications = createSurveyNotification(surveyStatuses);
          showNotification(notifications);
          sessionStorage.setItem("REJECTIONS_SHOWN", "true");
        }
      }
    });
  };

  const showNotification = (notifications) => {
    if (notifications.length > 0) {
      notification.warn({
        message: "Rejection Notes",
        description: (
          <ul style={{ textAlign: "left", fontFamily: "Inter" }}>
            {notifications.map((n) => (
              <li style={{ marginBottom: "10px" }}>{n}</li>
            ))}
          </ul>
        ),
        placement: "topRight",
        duration: 10,
        className: "dashboard-notification",
      });
    }
  };

  const createSurveyNotification = (surveyStatuses) => {
    let notifications = [];

    notifications = surveyStatuses?.survey_rejections.map((rejection) =>
      captaliseFirstWord(
        `[${decideWhatToShowForDate(new Date(rejection.rejected_at))}] - ${
          rejection.rejection_comment
        }.`
      )
    );

    return notifications;
  };

  const captaliseFirstWord = (senetence) =>
    senetence.at(0).toUpperCase() + senetence.substring(1);

  /**
   *
   * @param {Date} date
   */
  const decideWhatToShowForDate = (dateToDecide) => {
    const date = dateToDecide.getDate();
    const month = dateToDecide.getMonth();
    const year = dateToDecide.getFullYear();

    const today = new Date();

    const yesterday = new Date();
    yesterday.setDate(today - 1);

    if (
      date === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    ) {
      return `${moment(dateToDecide).startOf().fromNow()}`;
    } else if (
      date === yesterday.getDate() &&
      month === yesterday.getMonth() &&
      year === yesterday.getFullYear()
    ) {
      return `Yesterday, ${moment(dateToDecide).format("hh:mm a")}`;
    } else {
      return `${moment(dateToDecide).format("MM/DD/YYYY, HH:mm:ss")}`;
    }
  };
  const handleDomainClick = (domain) => {
    {
      if (csf_assessment_id) {
        setLocalStorage("SELECTED-SURVEY", {
          csf_survey_id: csf_assessment_id,
          csf_survey_status: csf_assessment_status,
        });
        if (csf_assessment_status === "completed") {
          dispatch(setAssessmentComplete(true)); // setting csf complete status
          dispatch(setSelectedDomain(domain)); //select domain for crr
          dispatch(setActiveSurvey(SURVEYS.CRR));
        } else {
          setLocalStorage("ACTIVE-SURVEY-TYPE", SURVEYS.CSF);
        }

        history.push({
          pathname: "/survey",
        });
      }
    }
  };

  const hasDashboardData =
    employeeDashboardData &&
    Object.keys(employeeDashboardData).length > 0;

  return (
    <div className="dashboard-wrapper">
      <div className="page-header">
        <div>
          <div className="heading-1">
            Welcome,{" "}
            {userData?.user_name ? userData?.user_name : userData?.name}
          </div>
          <div className="heading-2">
            {employeeDashboardData && employeeDashboardData["survey_name"]}
          </div>
        </div>
      </div>
      <ErrorAlert
        errorData={employeeDashboardError}
        message={
          employeeDashboardData &&
          Object.keys(employeeDashboardData).length == 0 &&
          "There are no active assessments. Kindly contact admin"
        }
      />
      {employeeDashboardLoading ? (
        <DashboardSkeleton />
      ) : (
        hasDashboardData && (
          <div className="employee-dashboard-grid employee-dashboard-enter">
            <div className="employee-dashboard-col employee-dashboard-col--list">
              <div className="heading-3">
                Select a domain to view the questionnaire
              </div>
              <div className="domain-cards-container">
                {employeeDashboardData.domains?.map((domain) => {
                  return (
                    <Card
                      style={{ marginTop: "10px" }}
                      hoverable
                      className={`dashboard-domains-list ${
                        domain?.write_access ? "" : "view-only-card"
                      }`}
                      key={domain.id}
                      onClick={() =>
                        selectedDomainIndex === domain.id
                          ? setSelectedDomainIndex(null)
                          : setSelectedDomainIndex(domain.id)
                      }
                    >
                      <div>
                        {!domain?.write_access ? (
                          <span className="view-only-tag">
                            <InfoOutlined fontSize="inherit" />
                            View only
                          </span>
                        ) : (
                          ""
                        )}
                        <div className="domain-dashboard">
                          <span className="dashboard-domain-id">
                            {domain.id < 10 ? "0" + domain.id : domain.id}{" "}
                          </span>
                          {domain.domain}
                        </div>
                        {selectedDomainIndex === domain.id && (
                          <div className="domain-description-dashboard">
                            <div>{domain.description}</div>
                            {getLocalStorage("SELECTED-SURVEY-STATUS")
                              ?.status === "In Progress" &&
                              domain.percentage_completed > 0 && (
                                <div className="percentage-completed">
                                  {parseInt(domain.percentage_completed)}%
                                  Complete
                                </div>
                              )}
                            <Button
                              variant="primary"
                              size="md"
                              style={{ marginTop: "10px" }}
                              onClick={() => handleDomainClick(domain)}
                            >
                              {getLocalStorage("SELECTED-SURVEY-STATUS")
                                ?.status !== "In Progress"
                                ? "VIEW ASSESSMENT"
                                : domain.percentage_completed > 1
                                ? "RETURN TO ASSESSMENT"
                                : "INITIATE ASSESSMENT"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
            <div className="employee-dashboard-col employee-dashboard-col--graph">
              <DashboardGraph />
            </div>
          </div>
        )
      )}
    </div>
  );
}
